/**
 * BillUpload — Cross-platform file upload for bills (PDF, JPG, PNG).
 * Matches Next.js UploadDropzone behavior:
 * - Web: drag-and-drop + click-to-select (via hidden input)
 * - Native: expo-document-picker tap-to-select
 * Posts FormData to /api/v1/upload/bill for AI analysis.
 */
import { useState, useRef, useEffect } from 'react'
import { View, Text, Pressable, StyleSheet, ActivityIndicator, Platform } from 'react-native'
import * as DocumentPicker from 'expo-document-picker'
import { Upload, CheckCircle, XCircle } from 'lucide-react-native'
import { apiClient } from '../api/client'
import { storage, TOKEN_KEY } from '../utils/storage'
import { colors, spacing, borderRadius, typography } from '../theme'
import { toast } from './Toast'

const API_URL = process.env.EXPO_PUBLIC_API_URL || 'http://localhost:8002'

interface BillUploadProps {
  userId: string
  onUploadComplete: (data: any) => void
}

export default function BillUpload({ userId, onUploadComplete }: BillUploadProps) {
  const [isUploading, setIsUploading] = useState(false)
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [isDragging, setIsDragging] = useState(false)
  const [fileName, setFileName] = useState<string | null>(null)
  const dropRef = useRef<View>(null)

  // Web drag-and-drop support
  useEffect(() => {
    if (Platform.OS !== 'web') return
    const el = (dropRef.current as any)?._nativeTag
      ? undefined
      : (dropRef.current as any)
    if (!el) return

    // Access the DOM node for web drag events
    const node = el instanceof HTMLElement ? el : (el as any)?.__nativeNode
    if (!node || !(node instanceof HTMLElement)) return

    const handleDragOver = (e: DragEvent) => { e.preventDefault(); setIsDragging(true) }
    const handleDragLeave = () => setIsDragging(false)
    const handleDrop = (e: DragEvent) => {
      e.preventDefault()
      setIsDragging(false)
      if (e.dataTransfer?.files?.length) {
        handleWebFile(e.dataTransfer.files[0])
      }
    }

    node.addEventListener('dragover', handleDragOver)
    node.addEventListener('dragleave', handleDragLeave)
    node.addEventListener('drop', handleDrop)
    return () => {
      node.removeEventListener('dragover', handleDragOver)
      node.removeEventListener('dragleave', handleDragLeave)
      node.removeEventListener('drop', handleDrop)
    }
  }, [])

  const handleWebFile = async (file: File) => {
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg']
    if (!validTypes.includes(file.type)) {
      toast.error('Kun PDF og billeder er tilladt')
      return
    }
    setFileName(file.name)
    setIsUploading(true)
    setStatus('idle')

    const formData = new FormData()
    formData.append('file', file)

    try {
      const token = await storage.get(TOKEN_KEY)
      const headers: Record<string, string> = {}
      if (token) headers['Authorization'] = `Bearer ${token}`

      const response = await fetch(
        `${API_URL}/api/v1/upload/bill?user_id=${userId}`,
        { method: 'POST', headers, body: formData }
      )
      if (!response.ok) throw new Error('Upload failed')
      const data = await response.json()
      setStatus('success')
      toast.success('Regning analyseret!')
      onUploadComplete(data)
    } catch {
      setStatus('error')
      toast.error('Kunne ikke analysere filen. Prøv igen eller indtast manuelt.')
    } finally {
      setIsUploading(false)
    }
  }

  const pickAndUpload = async () => {
    // On web, create hidden file input (matches Next.js click-to-select behavior)
    if (Platform.OS === 'web') {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = '.pdf,.jpg,.jpeg,.png'
      input.onchange = (e: any) => {
        const file = e.target?.files?.[0]
        if (file) handleWebFile(file)
      }
      input.click()
      return
    }

    // Native: use expo-document-picker
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ['application/pdf', 'image/jpeg', 'image/png'],
        copyToCacheDirectory: true,
      })

      if (result.canceled || !result.assets?.length) return

      const asset = result.assets[0]
      setFileName(asset.name)
      setIsUploading(true)
      setStatus('idle')

      const formData = new FormData()
      formData.append('file', {
        uri: asset.uri,
        name: asset.name,
        type: asset.mimeType || 'application/pdf',
      } as any)

      const response = await apiClient.upload.bill(userId, formData)
      setStatus('success')
      toast.success('Regning analyseret!')
      onUploadComplete(response.data)
    } catch {
      setStatus('error')
      toast.error('Kunne ikke analysere filen. Prøv igen eller indtast manuelt.')
    } finally {
      setIsUploading(false)
    }
  }

  const borderColor =
    isDragging ? colors.primary :
    status === 'success' ? colors.success :
    status === 'error' ? colors.error :
    colors.border

  const bgColor =
    isDragging ? colors.primaryLight :
    status === 'success' ? colors.successLight :
    status === 'error' ? colors.errorLight :
    colors.background

  return (
    <Pressable
      ref={dropRef as any}
      style={[s.container, { borderColor, backgroundColor: bgColor }]}
      onPress={pickAndUpload}
      disabled={isUploading}
    >
      {isUploading ? (
        <View style={s.content}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={s.statusText}>Analyserer din regning med AI...</Text>
          {fileName && <Text style={s.fileName}>{fileName}</Text>}
        </View>
      ) : status === 'success' ? (
        <View style={s.content}>
          <CheckCircle size={40} color={colors.success} />
          <Text style={[s.statusText, { color: colors.success }]}>Succes! Data fundet.</Text>
          <Pressable onPress={pickAndUpload}>
            <Text style={s.retryText}>Upload ny fil</Text>
          </Pressable>
        </View>
      ) : status === 'error' ? (
        <View style={s.content}>
          <XCircle size={40} color={colors.error} />
          <Text style={[s.statusText, { color: colors.error }]}>Fejl. Prøv igen.</Text>
          <Pressable onPress={pickAndUpload}>
            <Text style={s.retryText}>Prøv igen</Text>
          </Pressable>
        </View>
      ) : (
        <View style={s.content}>
          <View style={s.iconCircle}>
            <Upload size={24} color={colors.primary} />
          </View>
          <Text style={s.title}>Upload din regning</Text>
          <Text style={s.subtitle}>
            {Platform.OS === 'web' ? 'Træk filen herind eller klik for at vælge' : 'Tryk for at vælge en fil'}
          </Text>
          <Text style={s.formats}>PDF, JPG eller PNG</Text>
        </View>
      )}
    </Pressable>
  )
}

const s = StyleSheet.create({
  container: {
    borderWidth: 2, borderStyle: 'dashed', borderRadius: borderRadius.md,
    padding: spacing.lg, alignItems: 'center',
  },
  content: { alignItems: 'center', gap: spacing.sm },
  iconCircle: {
    width: 56, height: 56, borderRadius: 28,
    backgroundColor: colors.primaryLight, alignItems: 'center', justifyContent: 'center',
  },
  title: { ...typography.bodyBold, color: colors.text.primary },
  subtitle: { ...typography.caption, color: colors.text.secondary },
  formats: { fontSize: 11, color: colors.text.muted, marginTop: 4 },
  statusText: { ...typography.captionBold, color: colors.primary },
  fileName: { ...typography.small, color: colors.text.muted },
  retryText: { ...typography.captionBold, color: colors.primary, marginTop: 4 },
})
